import React, { useState, useCallback } from "react";
import { useCheckout } from "context/CheckoutContext";


const CardDetails: React.FC<any> = React.memo(() => {
  const { formData,setFormData } = useCheckout();

const cardData = formData.card;

  const [monthError, setMonthError] = useState<string>("");

  // Updated handler:
  // For numeric fields, we remove non-digit characters.
  // For text fields, we use the value as is so that any accidental spaces are preserved.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const newValue = ["expirationMonth", "expirationYear", "cardNumber", "cvv"].includes(name)
        ? value.replace(/\D/g, "")
        : value; // For non-numeric fields, keep the accidental spaces.
      
      setFormData((prevData:any) => {
        const updatedData = { ...prevData,card:{
          ...prevData.card,
          [name]: newValue }};
 
        return updatedData;
      });
    },
    [setFormData]
  );

  const handleMonthBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const month = parseInt(e.target.value, 10);
    if (isNaN(month) || month < 1 || month > 12) {
      setMonthError("Expiration month must be between 1 and 12.");
    } else {
      setMonthError("");
    }
  }, []);

  return (
    <>
      <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
        Enter Credit Card Details
      </h2>

      {/* Icons Row */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <img
            src="https://cdn4.iconfinder.com/data/icons/flat-brand-logo-2/512/visa-1024.png"
            alt="Visa"
            className="h-6"
          />
          <img
            src="https://cdn3.iconfinder.com/data/icons/payment-method-1/64/_Mastercard-512.png"
            alt="MasterCard"
            className="h-6"
          />
          <img
            src="https://cdn4.iconfinder.com/data/icons/payment-method/160/payment_method_american_express_card-512.png"
            alt="American Express"
            className="h-6"
          />
          <img
            src="https://cdn4.iconfinder.com/data/icons/logos-and-brands/512/93_Discover_Credit_Card_logo_logos-512.png"
            alt="Discover"
            className="h-6"
          />
        </div>
        <div className="flex items-center space-x-2">
          <img
            src="https://t3.ftcdn.net/jpg/05/30/57/82/360_F_530578201_0ymjKwOVhCkGHUsluky70pwtFaNJ6XwF.jpg"
            alt="SSL Secured"
            className="h-14"
          />
        </div>
      </div>

      {/* Cardholder Name */}
      <div>
        <label
          htmlFor="cardholderName"
          className="block text-sm text-neutral-500 dark:text-neutral-400"
        >
          Cardholder Name (required)
        </label>
        <input
          type="text"
          id="cardholderName"
          name="cardholderName"
          value={cardData?.cardholderName||""}
          onChange={handleChange}
          placeholder="Enter cardholder name"
          maxLength={27}
          autoComplete="cc-name"
          className="mt-1 block w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
          required
        />
      </div>

      {/* Card Number */}
      <div>
        <label
          htmlFor="cardNumber"
          className="block text-sm text-neutral-500 dark:text-neutral-400"
        >
          Card Number (required)
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id="cardNumber"
          name="cardNumber"
          value={cardData?.cardNumber}
          onChange={handleChange}
          placeholder="Enter card number"
          maxLength={19}
          autoComplete="cc-number"
          className="mt-1 block w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
          required
        />
      </div>

      {/* Expiration Date */}
      <div>
        <label className="block text-sm text-neutral-500 dark:text-neutral-400">
          Expiration Date (required)
        </label>
        <div className="grid grid-cols-2 gap-4 mt-1">
          <div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              id="expirationMonth"
              name="expirationMonth"
              value={cardData?.expirationMonth}
              onChange={handleChange}
              onBlur={handleMonthBlur}
              placeholder="MM"
              autoComplete="cc-exp-month"
              maxLength={2}
              className="block w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
              required
            />
            {monthError && <p className="text-red-500 text-xs mt-1">{monthError}</p>}
          </div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            id="expirationYear"
            name="expirationYear"
            value={cardData?.expirationYear}
            onChange={handleChange}
            placeholder="YYYY"
            autoComplete="cc-exp-year"
            maxLength={4}
            className="block w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
      </div>

      {/* CVV */}
      <div>
        <label
          htmlFor="cvv"
          className="block text-sm text-neutral-500 dark:text-neutral-400"
        >
          CVV (required)
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id="cvv"
          name="cvv"
          value={cardData?.cvv}
          onChange={handleChange}
          placeholder="Enter CVV"
          maxLength={4}
          autoComplete="cc-csc"
          className="mt-1 block w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
          required
        />
      </div>

      {/* Special Request Section */}
      <div>
        <label
          htmlFor="specialRequest"
          className="block text-sm text-neutral-500 dark:text-neutral-400"
        >
          Special Request (optional)
        </label>
        <textarea
          id="specialRequest"
          name="specialRequest"
          value={cardData?.specialRequest || ""}
          onChange={handleChange}
          placeholder="Enter any special requests..."
          rows={3}
          maxLength={250}
          className="mt-1 block w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
        />
      </div>
    </>
  );
});

export default CardDetails;